import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center space-x-2 mb-6">
              <TrendingUp className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">Finance Vitals</span>
            </Link>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Finance Vitals – Privacy Policy</h1>
            <p className="text-gray-600">Effective Date: January 1, 2026</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                This Privacy Policy explains how Finance Vitals ("we", "us", "our") collects, uses, stores, and protects personal information when you use our website and services ("Service").
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                By using the Service, you agree to the practices described in this policy.
              </p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">1.1 Information You Provide</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  When using the Service, you may provide:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                  <li>Name, email, login credentials</li>
                  <li>Uploaded financial documents (P&L, balance sheets, bank statements, exports)</li>
                  <li>Questionnaire responses</li>
                  <li>Payment information (processed securely by Stripe; we never see card numbers)</li>
                  <li>Support requests or messages</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">1.2 Automatically Collected Data</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We may collect:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Device information, browser type, IP address</li>
                  <li>Usage data (pages visited, time spent, error logs)</li>
                  <li>Cookies and analytics data (for improving service performance)</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We use your information to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                  <li>Provide financial health scoring and AI-powered analysis</li>
                  <li>Process payments for Deep Analysis or other services</li>
                  <li>Improve the accuracy and functionality of the platform</li>
                  <li>Communicate support updates or changes</li>
                  <li>Maintain security and prevent fraud</li>
                  <li>Comply with legal obligations</li>
                </ul>
                <p className="text-gray-700 leading-relaxed font-semibold">
                  We do not sell your personal information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Use of AI & Third-Party Processors</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  To generate financial insights, we may process documents or text you upload using third-party AI systems (e.g., OpenAI, Anthropic, or Emergent's infrastructure).
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  These processors may analyze data solely for the purpose of providing the Service.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We do not grant third-party AI providers the right to use or retain your uploaded data for training their models unless they already operate under strict privacy guarantees.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. File Storage & Retention</h2>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                  <li>Uploaded financial documents may be temporarily stored for processing</li>
                  <li>We delete documents once the Deep Analysis or scoring is complete, unless needed for:
                    <ul className="list-circle list-inside ml-6 mt-2 space-y-1">
                      <li>Debugging</li>
                      <li>Quality assurance</li>
                      <li>Security investigations</li>
                    </ul>
                  </li>
                  <li>Deep Analysis results, scores, and summaries may be retained in your account history</li>
                  <li>You may request deletion of stored personal data at any time (see Section 9)</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Sharing Your Information</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We may share your information with:
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Service Providers</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                  <li>Payment processors (Stripe)</li>
                  <li>AI providers for document analysis</li>
                  <li>Hosting providers</li>
                  <li>Security and support tools</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Professionals</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you connect with an accountant or bookkeeper through the platform, you choose whether to share your financial results or documents with them.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">5.3 Legal Obligations</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We may share information where required by law or to prevent fraud or harm.
                </p>

                <p className="text-gray-700 leading-relaxed font-semibold">
                  We do not sell or rent your information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Cookies & Tracking</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We may use cookies or similar technologies to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                  <li>Authenticate users</li>
                  <li>Improve performance</li>
                  <li>Measure analytics</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  You can disable cookies, but some features may not work.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Security</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use industry-standard technical and organizational measures to protect your data.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  However, no system is perfectly secure, and you use the Service at your own risk.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Children's Privacy</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The Service is not intended for individuals under the age of 18.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We do not knowingly collect data from minors.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Requesting Deletion or Access</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  You may request:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                  <li>Access to your personal data</li>
                  <li>Correction of inaccurate information</li>
                  <li>Deletion of stored documents or account data</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Email:</strong>{' '}
                  <a href="mailto:support@finance-vitals.com" className="text-blue-600 hover:underline" data-testid="privacy-support-email-link">
                    support@finance-vitals.com
                  </a>
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We will respond within a reasonable timeframe.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. International Users</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Finance Vitals is operated from the United States.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  If you access the Service from outside the U.S., your data may be transferred to the United States for processing.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to This Privacy Policy</h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update this Policy as needed. Continued use after changes indicates acceptance.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Information</h2>
                <p className="text-gray-700 leading-relaxed mb-2">
                  <strong>Finance Vitals</strong>
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <a href="mailto:support@finance-vitals.com" className="text-blue-600 hover:underline" data-testid="privacy-contact-support-email-link">
                    support@finance-vitals.com
                  </a>
                </p>
              </section>
            </div>

            {/* Footer Navigation */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
                  ← Back to Home
                </Link>
                <div className="space-x-4">
                  <Link to="/terms" className="text-gray-600 hover:text-gray-800">
                    Terms of Use
                  </Link>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-500">© 2026 Finance Vitals</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;